import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationBehaviorOptions, Router } from '@angular/router';
import { paymentFailedPath } from '../../services/payment.service';
import { FireService } from '../../services/fire.service';
import { FSPayment } from '../../models/PaymentModels';
import MoneyUtils, { HackUtils } from '../../utils/Utils';
import { combineLatest, from, Subscription } from 'rxjs';
import { OrderService } from '../../services/order.service';
import { delay, first, skip } from 'rxjs/operators';
import { MessengerService } from '../../services/messenger.service';
import { AuthService } from '../../services/auth.service';
import {AuthorizedApiService} from "../../services/authorized-api.service";
import {environment} from "../../../environments/environment";
import {TranslateService} from "../../services/translate.service";
import {AppStateService} from '../../services/app-state.service';
import {ModalService} from '../../services/modal.service';
import {SnackbarService} from '../../services/snackbar.service';
import moment from 'moment';
import {AppState} from '../../models/AppState';
import {LoadingSpinnerComponent} from '../ui/loading-spinner-component';
import {NgIf} from '@angular/common';
import {CircleButtonComponent} from '../buttons/circle-button.component';
import {ReceiptDisplayComponent} from '../ui/receipt-display.component';
import {LightIconButtonComponent} from '../buttons/light-icon-button-component';
import {SwishNumberDialogComponent} from '../dialogs/swish-number-dialog.component';
import { PageButtonComponent } from "../buttons/page-button.component";
import { UrlHistoryService } from '../../services/url.history.service';
import {PaymentCardTitleComponent, PaymentSwishTitleComponent, PaymentVippsTitleComponent} from '../buttons/payment-button.component';
import {ReceiptPromptDialogComponent} from '../dialogs/receipt-prompt-dialog.component';

@Component({
  selector: 'app-pay-status',
  standalone: true,
  templateUrl: './pay-status.component.html',
  imports: [
    LoadingSpinnerComponent,
    NgIf,
    CircleButtonComponent,
    ReceiptDisplayComponent,
    LightIconButtonComponent,
    PageButtonComponent,
    PaymentCardTitleComponent,
    PaymentSwishTitleComponent,
    PaymentVippsTitleComponent
  ],
  styleUrls: ['./pay-status.component.css']
})
export class PayStatusComponent implements OnInit, OnDestroy {
  isLoading = true;
  private receiptKey: string|null = null;
  payment?: FSPayment;
  typeImage?: string;
  titleType?: string;
  amountFormatted!: string;

  // Viva callbacks are not guaranteed to be received in the demo environment, let the user manually click a button to check
  vivaDemoIssue = false;
  private paySub?: Subscription;
  private combinedSub?: Subscription;
  private timeSub?: Subscription;
  swishQR?: string;
  private venueId;
  private isKiosk: boolean = false;
  private vivaParamOrderCode: string|null = null;
  private vivaParamTxId: string|null = null;
  private plKey: string|null = null;  // payment link the guest came from, if any (query param pl)
  private from: string|null = null;   // the page the payment started on (query param from: cart or livesplit)
  payWithNumber = false;
  noticeSwishAuto = false;
  private state?: AppState;
  showInfo = false;

  constructor(private route: ActivatedRoute, private fire: FireService,
              private order: OrderService, private messengerService: MessengerService,
              private auth: AuthService, private appState: AppStateService,
              private modalService: ModalService, private router: Router,
              private snackbar: SnackbarService, private apiService: AuthorizedApiService,
              public translate: TranslateService, private url: UrlHistoryService) {
    this.venueId = this.auth.venueId!;
  }

  ngOnInit(): void {
    this.isKiosk = this.auth.isKioskMode();
    console.log("PayStatusComponent init");

    // Important to subscribe to app state here, so that magic sign in
    // stuff can happen before listening to payment status
    this.combinedSub = combineLatest([
      this.route.paramMap,
      this.route.queryParamMap,
      this.appState.observeAppState()]
    ).pipe(first()).subscribe(res => {
      console.log("PayStatusComponent combined", res);
      this.state = res[2];

      this.vivaParamOrderCode = res[1].get("s");
      this.vivaParamTxId = res[1].get("t");
      this.plKey = res[1].get("pl");
      this.from = res[1].get("from");
      if (this.vivaParamOrderCode && this.vivaParamTxId) {
        setTimeout(() => {
          this.vivaDemoIssue = true;
        }, environment.viva === "demo" ?4000:10000);
        console.log("Redirected from viva payment");
        this.resolveReceiptKey(this.vivaParamOrderCode, this.vivaParamTxId);
      } else {
        console.log("--- paystatus res", res);
        const fullReceiptKey = res[0].get("receipt_key");
        //console.log("Full receipt key", fullReceiptKey);
        if (fullReceiptKey) {
          const url = new URL(fullReceiptKey, window.location.origin);
          this.receiptKey = url.pathname.startsWith('/') ? url.pathname.slice(1) : url.pathname;// Extracts the path without query parameters
          console.log("Receipt key", this.receiptKey);
        }
        this.beginObserving();
      }
    });
  }

  private updateSwishNotice() {
    if (this.payWithNumber) {
      // Payment is already in progress, do not show the notice again
      return;
    }

    const messengerSDKInfo = this.messengerService.getMessengerState();
    const sdkLoaded = messengerSDKInfo === "IN_MESSENGER";
    const isMessenger = this.state!.order.user_cfg.user_type === "messenger";
    const isIphone = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if ((isMessenger || sdkLoaded) && isIphone) {
      this.isLoading = false;
      this.noticeSwishAuto = true;
    }
  }

  private beginObserving() {
    this.paySub = this.fire.observePayment(this.receiptKey!).subscribe(payment => {
      console.log(payment);
      if (payment == null) {
        console.log(`Payment ${this.receiptKey} not found...`);
      } else if (payment.state === 'new') {
        this.payment = payment;
        if (payment.type==='swish') {
          this.updateSwishNotice();
        }
        // Vipps: no timeout or QR here; the server mirrors declined when the payment expires (10 min at Vipps)
        this.titleType = this.payment.type === "swish" ? "swish" : this.payment.type === "vipps" ? "vipps" : "card";
        this.amountFormatted = MoneyUtils.formatCent(payment.amount_cent);
        if (this.payment.type === "swish") {
          this.checkSwishRequestAge();
          if (this.auth.kioskMode) {
            this.showSwishQR();
          }
        }
      } else if (payment.state === 'paid') {
        if(this.isKiosk) {
          const kioskState = this.auth.getKioskState();
          if (kioskState === "monter") {
            this.fetchOrderAndExit("menu/food");
            this.openReceiptAskDialog(this.receiptKey!);
          } else {
            // Standard kiosk mode - go directly to feed
            this.fetchOrderAndExit("feed");
          }
        } else {
          // If payment is done in a open order scenario redirect to settlement eles feed
          if (this.state?.cfg?.voucher?.collins_voucher_mode === "modern") {
            this.fetchOrderAndExit("vouchers");
          } else {
            const path = this.state?.order?.user_cfg?.payment?.open_order ? `settlement/${this.receiptKey}` : "feed";
            this.fetchOrderAndExit(path);
          }
        }
      } else {
        if (this.isKiosk) {
          this.fetchOrderAndExit("cart");
          //this.snackbar.open("Betalningen misslyckades", "OK", { duration: 5000 });
        } else {
          this.returnToSender(payment.type);
        }
      }
    });
  }

  private fetchOrderAndExit(path = "feed", extras?: NavigationBehaviorOptions) {
    this.timeSub?.unsubscribe();
    this.order.fetchCurrentOrder().subscribe(o => {
      this.router.navigateByUrl(`${this.auth.venueId}/${path}`, extras);
    });
  }

  cancel() {
    this.url.navigateBack();
  }

  ngOnDestroy(): void {
    this.combinedSub?.unsubscribe();
    this.paySub?.unsubscribe();
    this.timeSub?.unsubscribe();
  }

  showSwishQR() {
    this.swishQR = this.payment!.qr;
    console.log(this.swishQR);
  }

  private checkSwishRequestAge() {
    const diff = moment().diff(moment(this.payment!.date.toDate()), "seconds");
    if (diff < 3 * 60) {
      const msLeft = (3 * 60 - diff) * 1000;
      console.log(`Swish will timeout in ${msLeft} ms`);
      this.timeSub = from([1]).pipe(delay(msLeft)).subscribe(r => this.fetchOrderAndExit("cart"));
    } else {
      console.log(`Swish age ${diff} sec`);
      this.fetchOrderAndExit("cart");
    }
  }

  private returnToSender(paymentType?: string) {
    if (paymentType === 'vipps') {
      // Vipps is only offered when settling a bill; declined = aborted in the app or expired (10 min), and the
      // guest may arrive here through the Vipps return URL, so land on a real page with a retry button. The
      // payment link key is passed on so "try again" reopens the link and not an empty table bill.
      const pl = this.plKey ? `?pl=${encodeURIComponent(this.plKey)}` : "";
      this.router.navigateByUrl(`${this.auth.venueId}/livesplit/paymentfailed${pl}`);
      return;
    }
    // The page the payment started on has the failure view (there is no venue-level one): the cart or the bill,
    // and the payment link key rides along so "try again" reopens the link.
    this.router.navigateByUrl(`${this.auth.venueId}/${paymentFailedPath(this.from, this.plKey)}`, { skipLocationChange: true });
  }

  private openReceiptAskDialog(rkey: string) {
    this.modalService.show(ReceiptPromptDialogComponent, {
      data: {
        receiptKey: rkey
      }
    }).subscribe(result => {
      if (result) {
        this.router.navigateByUrl(`${this.venueId}/receipt/${rkey}`);
      } else {
        this.router.navigateByUrl(`${this.venueId}/menu/food`);
      }
    });
  }

  private resolveReceiptKey(orderCode: string, transactionId: string) {
    this.apiService.vivaResolveReceiptKey(orderCode, transactionId).subscribe(res => {
      if (res) {
        this.receiptKey = res.receipt_key;
        this.beginObserving();
      } else {
        this.router.navigateByUrl(`${this.venueId}/feed`);
      }
    });
  }

  forceVivaTxCheck() {
    this.apiService.vivaCheckOrderStatus(this.vivaParamOrderCode!, this.vivaParamTxId!).subscribe(res => {
      // if transaction is paid it will be updated, and the read in the observer
    });
  }

  showSwishPhone() {
    this.modalService.show(SwishNumberDialogComponent, {
      data: {
        phone: this.appState.snapshot?.order.user_cfg.swish_number,
        isKiosk: this.isKiosk,
        sendFunction: (phone: string, savePhone: boolean) => {
          this.isLoading = true;
          this.apiService.swishLink(0, undefined, this.receiptKey!, undefined, undefined, undefined, undefined, phone, savePhone).subscribe(res => {
            this.noticeSwishAuto = false;
            this.payWithNumber = true;
            this.modalService.close();
          }, error => {
            console.log(error.error);
            const message = error.error?.message || "Det gick inte att påbörja swish-betalning";
            this.isLoading = false;
            this.snackbar.showMessage(message);
          });
        }
      }
    });
  }

  getString(s: string, pc: string = "normal") {
    return this.translate.single(s, pc);
  }

  setShowInfo(event: Event): void {
    event.preventDefault(); // Prevent the default link behavior
    this.showInfo = true;
  }
}
